import { InviteDialogActions } from "@bezoned/shared/components/invite-dialog/invite-dialog-actions.jsx";
import { InviteView } from "@bezoned/shared/components/invite-view.jsx";
import { useInviteForm } from "@bezoned/shared/hooks/use-invite-form.js";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamMemberCreateResponse } from "@bezoned/shared/types/api/team/team-member-create.types.js";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useOffice } from "../../../../features/office/use-office.js";
import {
  getOrganizationUsers,
  teamMemberCreate,
} from "../../../../services/api.service.js";
import { recordErrorInSentry } from "../../../../utils/init-sentry.js";
import { BaseModal } from "../base-modal.jsx";
import type { SharedModalProps } from "../base-modal.jsx";

const getTeamMemberCreateError = (data: TeamMemberCreateResponse) => {
  if (data.results.failed.length === 0) {
    return null;
  }

  return new Error(
    "Failed to add user. This could be due to permissions. Please contact your team admin."
  );
};

export const InviteModalRegularVariant = ({ onClose }: SharedModalProps) => {
  const { team } = useOffice();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const searchQuery = useQuery({
    enabled: searchTerm !== null,
    queryFn: () => getOrganizationUsers({ search: searchTerm ?? undefined }),
    queryKey: ["organizationUsers", searchTerm],
  });

  const handleInviteUser = async (msUserId: string) => {
    try {
      if (!team) {
        throw new Error("Team is not available");
      }

      const data = await teamMemberCreate({
        msTeamId: team.msTeamId,
        msUserIds: [msUserId],
      });
      const error = getTeamMemberCreateError(data);

      if (error) {
        throw error;
      }
    } catch (error) {
      recordErrorInSentry(error);
      throw error;
    }
  };
  const handleClose = useStableCallback(() => onClose?.());
  const inviteForm = useInviteForm({
    onClose: handleClose,
    onInviteUser: handleInviteUser,
    onSearchChange: setSearchTerm,
  });

  return (
    <BaseModal
      actionComponent={
        <InviteDialogActions
          controller={inviteForm}
          onClose={inviteForm.handleClose}
        />
      }
      onClose={inviteForm.handleClose}
      title="Invite"
    >
      <InviteView
        controller={inviteForm}
        description={
          <>
            Start typing a name or email of a person inside your organization.
            The person will automatically be invited and notified inside
            Microsoft Teams.
          </>
        }
        searchErrorMessage={
          searchQuery.error
            ? "Something failed while searching for users. Please try again"
            : null
        }
        searchResults={searchQuery.data?.users ?? []}
      />
    </BaseModal>
  );
};
